import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/base/AnimatedSection';
import AnimatedImage from '../../components/base/AnimatedImage';
import Footer from '../../components/feature/Footer';

export default function LandscapeTurfPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="stagger-children">
              <span className="inline-block bg-emerald-600 text-white text-xs font-bold px-4 py-2 rounded-md mb-6 uppercase tracking-wide">Landscape Turf</span>
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">Beautiful Lawns Without The Maintenance</h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Transform your Tulsa property with premium landscape turf that looks and feels like natural grass. Perfect for residential lawns, commercial properties, and any outdoor space where you want lush, green beauty year-round.
              </p>
              <div className="flex flex-wrap gap-5">
                <a href="tel:918-806-8873" className="bg-green-700 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-green-800 hover:shadow-xl transition-all duration-200 whitespace-nowrap" style={{ transform: 'translateY(0)' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  Get A Quote
                </a>
                <Link to="/about" className="bg-transparent border-2 border-green-700 text-green-700 px-8 py-4 rounded-full text-base font-semibold hover:bg-green-700 hover:text-white transition-all duration-200 whitespace-nowrap">
                  Learn More
                </Link>
              </div>
            </div>
            <AnimatedImage
              src="https://public.readdy.ai/ai/img_res/c245bf2cb885b2acefab39ea3ae40670.jpg" 
              alt="Landscape Turf Tulsa Oklahoma"
              className="rounded-2xl shadow-2xl w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <AnimatedSection className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 stagger-children">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose Landscape Turf</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Enjoy a perfect lawn every day of the year with zero mowing, watering, or fertilizing</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              { icon: 'ri-water-flash-line', title: 'Save Water & Money', description: 'Eliminate water bills for lawn irrigation. Save thousands of gallons annually—critical in Oklahoma\'s hot, dry summers. Your synthetic lawn stays green without a drop of water.' },
              { icon: 'ri-time-line', title: 'Zero Maintenance', description: 'No more mowing, edging, fertilizing, or weeding. Spend your weekends enjoying your yard instead of working on it. Landscape turf requires minimal upkeep—just occasional rinsing.' },
              { icon: 'ri-sun-line', title: 'Year-Round Beauty', description: 'Your lawn stays vibrant green through Tulsa\'s scorching summers, cold winters, and everything in between. No brown patches, no dormant seasons—just consistent beauty.' },
              { icon: 'ri-leaf-line', title: 'Eco-Friendly Choice', description: 'Eliminate harmful pesticides, herbicides, and fertilizers from your property. No gas-powered mowers or trimmers. Reduce your carbon footprint while maintaining a beautiful lawn.' },
              { icon: 'ri-home-smile-line', title: 'Increase Property Value', description: 'Boost curb appeal with a consistently perfect lawn. Synthetic turf is a selling point for Tulsa homes, showing buyers a low-maintenance, water-saving landscape solution.' },
              { icon: 'ri-shield-check-line', title: 'Durable & Long-Lasting', description: 'Our landscape turf is engineered to withstand heavy foot traffic, Oklahoma weather extremes, and UV exposure. Backed by a 10-year warranty for lasting performance.' }
            ].map((benefit, index) => (
              <div 
                key={benefit.title}
                className="text-center"
                style={{ 
                  opacity: 0,
                  animation: 'fadeIn 400ms ease-out forwards',
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                  <i className={`${benefit.icon} text-6xl text-green-700`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Product Features */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=close-up%20detailed%20view%20of%20premium%20artificial%20synthetic%20landscape%20turf%20grass%20blades%2C%20realistic%20texture%20and%20color%20variation%2C%20emerald%20green%20fake%20grass%20fibers%2C%20professional%20quality%20turf%20material%2C%20clean%20simple%20background%20showing%20grass%20detail&width=800&height=900&seq=turfdetail918&orientation=portrait" 
                alt="Premium Landscape Turf Detail"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Premium Quality Features</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Realistic Appearance</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Multi-toned grass blades with natural color variation create an authentic look that's virtually indistinguishable from real grass. Thatch layer adds depth and realism.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Soft &amp; Comfortable</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Engineered with soft, non-abrasive fibers that feel great underfoot. Perfect for barefoot walking, children playing, and outdoor entertaining.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Superior Drainage</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Advanced drainage system allows water to flow through quickly—up to 30 inches per hour. No puddles or standing water, even during heavy Oklahoma rainstorms.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">UV Stabilized</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Protected against Oklahoma's intense sun with advanced UV inhibitors. Maintains vibrant color and structural integrity for years without fading or degrading.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Lead-Free &amp; Safe</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Made in USA with non-toxic materials. Safe for children, pets, and the environment. Meets all safety standards and certifications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Perfect For Any Tulsa Property</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Our landscape turf transforms residential and commercial spaces throughout the Tulsa area</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20residential%20front%20yard%20with%20artificial%20turf%20lawn%20in%20Tulsa%20Oklahoma%20suburban%20home%2C%20modern%20house%20exterior%2C%20synthetic%20grass%20landscape%2C%20clean%20manicured%20appearance%2C%20simple%20background&width=600&height=600&seq=residential918&orientation=squarish" 
                  alt="Residential Landscape Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Residential Lawns</h3>
                <p className="text-sm text-gray-600">Front yards, backyards, and side yards for Tulsa homes</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20commercial%20building%20with%20artificial%20turf%20landscaping%20in%20Tulsa%20Oklahoma%2C%20business%20property%20with%20synthetic%20grass%2C%20professional%20landscape%20design%2C%20office%20building%20exterior%2C%20clean%20simple%20background&width=600&height=600&seq=commercial918&orientation=squarish" 
                  alt="Commercial Landscape Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Commercial Properties</h3>
                <p className="text-sm text-gray-600">Office buildings, retail centers, and business parks</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=children%20playground%20with%20safe%20artificial%20turf%20surface%20in%20Tulsa%20Oklahoma%2C%20synthetic%20grass%20play%20area%2C%20colorful%20playground%20equipment%2C%20kid-friendly%20fake%20grass%2C%20clean%20simple%20background&width=600&height=600&seq=playground918&orientation=squarish" 
                  alt="Playground Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Playgrounds</h3>
                <p className="text-sm text-gray-600">Safe, cushioned surfaces for schools and parks</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=rooftop%20terrace%20with%20artificial%20turf%20grass%20in%20Tulsa%20Oklahoma%20urban%20setting%2C%20synthetic%20grass%20on%20rooftop%20deck%2C%20modern%20outdoor%20living%20space%2C%20city%20view%2C%20clean%20simple%20background&width=600&height=600&seq=rooftop918&orientation=squarish" 
                  alt="Rooftop Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Rooftops &amp; Patios</h3>
                <p className="text-sm text-gray-600">Create green spaces in unexpected places</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation */}
      <section className="py-24 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Professional Installation Or DIY</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We support both professional installers and DIY homeowners with everything you need</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">For Contractors &amp; Installers</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Wholesale pricing with volume discounts</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Fast delivery throughout Tulsa metro area</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Complete installation supplies and tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Technical support and product expertise</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Flexible payment terms for qualified contractors</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">For DIY Homeowners</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Detailed installation guides and videos</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Expert advice from our knowledgeable team</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">All supplies available: adhesive, seaming tape, infill</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Tool recommendations and rental options</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-700 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Ongoing support throughout your project</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Ready For A Perfect Lawn?</h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Get a free quote on premium landscape turf for your Tulsa property. Our team will help you choose the perfect product and calculate exactly what you need.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href="tel:918-806-8873" className="bg-white text-green-700 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap">
              Call 918-806-8873
            </a>
            <Link to="/about" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white hover:text-green-700 transition-all whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}